import React, { useEffect, useRef, useState } from "react";
import Modal from "components/modal/Modal";
import closeIcon from "assets/images/Close.svg";
import "./spanish-acknowledgment-modal.scss";
import { ENGLISH_LEGAL_COPY, SPANISH_LEGAL_COPY } from "utilities/Constant";

export type TabType = "spanish" | "english";

export interface SpanishAcknowledgmentModalProps {
  readonly onAcknowledge: () => void;
  readonly onCancel: () => void;
}

const SPANISH_TAB: TabType = "spanish";
const ENGLISH_TAB: TabType = "english";


const TAB_LABELS = {
  spanish: "Español",
  english: "English",
};

const CTA_LABELS = {
  spanish: "Reconocer y Continuar",
  english: "Acknowledge and Continue",
};

const MODAL_TITLE = "Spanish Application Acknowledgement Acuse de recibo de solicitud";

export default function SpanishAcknowledgmentModal({
  onAcknowledge,
  onCancel,
}: SpanishAcknowledgmentModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>(SPANISH_TAB);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!modalRef.current) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      if (!focusableElements.length) {
        return;
      }

      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstFocusableElement) {
        event.preventDefault();
        lastFocusableElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastFocusableElement) {
        event.preventDefault();
        firstFocusableElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const firstFocusableElement = modalRef.current?.querySelector<HTMLElement>("button");
    firstFocusableElement?.focus();
  }, []);

  const getCtaLabel = (): string => {
    return activeTab === SPANISH_TAB ? CTA_LABELS.spanish : CTA_LABELS.english;
  };

  const customHeader = (
    <div className="spanish-acknowledgment-modal__header">
      <h3 id="spanish-acknowledgment-modal-title" className="spanish-acknowledgment-modal__title">
        <span className="spanish-acknowledgment-modal__title-main">Spanish Application Acknowledgement</span>
        <span className="spanish-acknowledgment-modal__title-subtitle">Acuse de recibo de solicitud</span>
      </h3>
      <button
        type="button"
        className="spanish-acknowledgment-modal__close-btn"
        onClick={onCancel}
        aria-label="Close modal"
      >
        <img src={closeIcon} alt="" />
      </button>
    </div>
  );

  const customBody = (
    <div className="spanish-acknowledgment-modal__body">
      <div className="spanish-acknowledgment-modal__tabs" role="tablist" aria-label="Acknowledgment language">
        <button
          id="spanish-tab"
          type="button"
          className={`spanish-acknowledgment-modal__tab ${
            activeTab === SPANISH_TAB ? "spanish-acknowledgment-modal__tab--active" : ""
          }`}
          onClick={() => setActiveTab(SPANISH_TAB)}
          role="tab"
          aria-selected={activeTab === SPANISH_TAB}
          aria-controls="spanish-tab-panel"
          tabIndex={activeTab === SPANISH_TAB ? 0 : -1}
        >
          {TAB_LABELS.spanish}
        </button>
        <button
          id="english-tab"
          type="button"
          className={`spanish-acknowledgment-modal__tab ${
            activeTab === ENGLISH_TAB ? "spanish-acknowledgment-modal__tab--active" : ""
          }`}
          onClick={() => setActiveTab(ENGLISH_TAB)}
          role="tab"
          aria-selected={activeTab === ENGLISH_TAB}
          aria-controls="english-tab-panel"
          tabIndex={activeTab === ENGLISH_TAB ? 0 : -1}
        >
          <span className="GlobalLinkNoTx">{TAB_LABELS.english}</span>
        </button>
      </div>

      <div
        className="spanish-acknowledgment-modal__content"
        id={activeTab === SPANISH_TAB ? "spanish-tab-panel" : "english-tab-panel"}
        role="tabpanel"
        aria-labelledby={activeTab === SPANISH_TAB ? "spanish-tab" : "english-tab"}
      >
        {activeTab === SPANISH_TAB ? (
          <p className="spanish-acknowledgment-modal__legal-text">{SPANISH_LEGAL_COPY}</p>
        ) : (
          <p className="spanish-acknowledgment-modal__legal-text GlobalLinkNoTx">
            {ENGLISH_LEGAL_COPY}
          </p>
        )}
      </div>
    </div>
  );

  const cancelLabel = activeTab === SPANISH_TAB ? "Cancelar" : "Cancel";

  const footer = (
    <div className="spanish-acknowledgment-modal__footer">
      <button
        type="button"
        className="spanish-acknowledgment-modal__cancel-cta"
        onClick={onCancel}
      >
        {activeTab === ENGLISH_TAB ? <span className="GlobalLinkNoTx">{cancelLabel}</span> : cancelLabel}
      </button>
      <button
        type="button"
        className="spanish-acknowledgment-modal__acknowledge-cta"
        onClick={onAcknowledge}
      >
        {activeTab === ENGLISH_TAB ? <span className="GlobalLinkNoTx">{getCtaLabel()}</span> : getCtaLabel()}
      </button>
    </div>
  );

  return (
    <div
      className="spanish-acknowledgment-modal"
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="spanish-acknowledgment-modal-title"
    >
      <Modal
        customHeader={customHeader}
        customBody={customBody}
        footer={footer}
        footerContentPosition="center"
      />
    </div>
  );
}
